import type { BannerPiece } from "@workspace/crystallize/generated/discovery/";
import { Button } from "@workspace/ui/components/button";
import type React from "react";
import { Link } from "react-router";
import CrystallizeResponsiveImage from "../product/crystallize-responsive-image";

export const HeroBanner: React.FC<
  BannerPiece & { variant?: "primary" | "secondary" }
> = ({ image, title, description, buttons, variant = "primary" }) => {
  const firstImage = image?.[0];

  const primary = () => {
    return (
      <section className="relative w-full py-24 sm:py-32 md:py-40 lg:py-48 xl:py-56">
        {firstImage && (
          <CrystallizeResponsiveImage
            image={firstImage}
            className="absolute inset-0 z-5 h-full w-full object-cover"
            fetchPriority="high"
            //TODO: Add better sizes for performance
            sizes="100vw"
          />
        )}
        <div className="absolute inset-0 z-8" />
        <div className="container relative z-10 px-4 md:px-6">
          <div className="flex flex-col items-center space-y-4 text-center">
            <div className="space-y-2">
              {title && (
                <h1 className="font-bold text-3xl text-white tracking-tighter sm:text-4xl md:text-5xl lg:text-6xl/none">
                  {title}
                </h1>
              )}
              <p className="mx-auto max-w-[700px] font-bold text-white md:text-xl">
                {description}
              </p>
            </div>
            {buttons && (
              <div className="grid w-full max-w-4xl grid-cols-[repeat(auto-fit,minmax(min(100%,14rem),1fr))] gap-4">
                {buttons.map((button) => (
                  <Button
                    key={button?.link?.items?.[0]?.path}
                    asChild
                    size="lg"
                    className="h-auto min-h-10 w-full min-w-0 whitespace-normal break-words px-4 text-center sm:px-6"
                  >
                    <Link
                      to={button?.link?.items?.[0]?.path || ""}
                      prefetch="intent"
                    >
                      {button?.text}
                    </Link>
                  </Button>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>
    );
  };

  return (
    <section className="container flex flex-col items-center justify-between gap-6 px-4 md:flex-row md:gap-6 md:px-0">
      <div className="w-full md:flex-1">
        <div className="flex flex-col items-center space-y-4 text-center md:text-left">
          <div className="space-y-2">
            {title && (
              <h1 className="text-4xl text-primary tracking-tighter sm:text-4xl md:text-5xl lg:text-6xl/none">
                {title}
              </h1>
            )}
            <p className="mx-auto max-w-prose text-primary md:text-xl">
              {description}
            </p>
          </div>
          {buttons && (
            <div className="grid w-full max-w-4xl grid-cols-[repeat(auto-fit,minmax(min(100%,14rem),1fr))] gap-4">
              {buttons.map((button) => (
                <Button
                  key={button?.link?.items?.[0]?.path}
                  asChild
                  size="lg"
                  className="h-auto min-h-10 w-full min-w-0 whitespace-normal break-words px-4 text-center sm:px-6"
                >
                  <Link
                    to={button?.link?.items?.[0]?.path || ""}
                    prefetch="intent"
                  >
                    {button?.text}
                  </Link>
                </Button>
              ))}
            </div>
          )}
        </div>
      </div>
      {firstImage && (
        <CrystallizeResponsiveImage
          image={firstImage}
          className="h-full max-h-[600px] w-full rounded-xl object-cover shadow-lg"
          fetchPriority="high"
          sizes="(min-width: 768px) 35vw, 100vw"
        />
      )}
    </section>
  );
};
